/**
 * 发货单管理
 */
import type { PageContext } from '@zto/zpage'
import { logDialog, batchChangeDialog } from './_dialogs'
import { UI_PAGE_EVENTS } from '@zto/zpage-ui-element'
import dayjs from 'dayjs'
import { commonOptions } from '@/config/options'
import { ShipmentStatusName, ShipmentStatus, OutTypeName, RbacRoleScopeType } from '@/config/enums'
import { uuid } from '@zto/zpage'
import { ElLoading } from 'element-plus'

export default {
  body: {
    type: 'crud',
    actions: {
      query: {
        api: '/cabinet/admin/oms/AdminOmsShipmentQueryController/queryPage'
      },
      add: {
        label: '新增发货单',
        link: {
          title: '新增发货单',
          name: 'tuda.cabinet.shipment.add',
          path: '/cabinet/shipment/add',
          query: {
            type: 'add'
          }
        }
      },
      detail: {
        label: '详情',
        link: {
          title: '发货单详情（${data.row.shipmentCode}）',
          name: 'tuda.cabinet.shipment.detail',
          path: '/cabinet/shipment/detail',
          query: {
            shipmentCode: '${data.row.shipmentCode}'
          }
        }
      },
      batchChange: {
        label: '批量改价',
        api: '/cabinet/admin/oms/AdminOmsShipmentCommandController/batchUpdatePrice',
        message: false,
        payload: async ({ app, data }: PageContext, ext: any) => {
          const { Message, MessageBox } = app.useMessage()
          const selectedRows = ext?.selection
          if (selectedRows === undefined) {
            return false
          }
          if (!selectedRows?.length) {
            Message.warning('请至少选择一条数据！')
            return false
          }
          if (Array.isArray(selectedRows)) {
            const shipList = selectedRows?.map((item: any) => ({
              shipmentCode: item.shipmentCode,
              productCode: item.productCode
            }))
            // 检查是否有不同的 productCode
            const productCodes = shipList.map(item => item.productCode)
            const uniqueProductCodes = new Set(productCodes)
            if (uniqueProductCodes.size > 1) {
              Message.warning('所选数据包含不同的商品编码，无法批量改价！')
              return false
            }
            return {
              shipList
            }
          }
        },
        dialog: batchChangeDialog
      },
      import: {
        label: '批量发货',
        api: '/cabinet/admin/oms/AdminOmsShipmentCommandController/batchShip',
        apiParams: () => ({ batchNum: uuid() }),
        template: 'operate_objective_cityIndex',
        maxCount: 1000,
        clearBeforeShow: true // 显示弹窗前清除文件
      },
      close: {
        label: '关闭',
        api: '/cabinet/admin/oms/AdminOmsShipmentCommandController/close',
        payload: {
          shipmentCodes: ['${data.row.shipmentCode}']
        },
        message: '确认关闭吗？'
      },
      log: {
        label: '日志',
        dialog: logDialog
      },
      customExport: {
        label: '导出全部',
        customAction: true, // 重要：确保 onAction 被执行
        message: false, // 重要：不显示确认对话框，直接执行
        reload: false, // 导出成功后不重新请求列表接口
        payload: async (ctx: PageContext, ext: any) => {
          const searchParams = ext?.searchParams
          return {
            ...searchParams
          }
        },
        onAction: async (ctx: PageContext, actionCfg: any) => {
          const { app } = ctx
          const { Message } = app.useMessage()

          // 显示全屏loading
          const loadingInstance = ElLoading.service({
            lock: true,
            text: '正在导出数据，请稍候...',
            background: 'rgba(0, 0, 0, 0.7)'
          })

          try {
            const searchParams: any = ctx.data || {}

            const size = 1000 // 每页大小
            let current = 1 // 从第1页开始
            let lastId = null
            let allData: any[] = [] // 存储所有数据
            let hasMoreData = true // 是否还有更多数据

            // 循环查询直到数据为空或报错
            while (hasMoreData) {
              try {
                const res = await app.request({
                  url: '/cabinet/admin/oms/AdminOmsShipmentQueryController/queryForExport',
                  data: {
                    ...searchParams,
                    current: current,
                    size: size,
                    lastId: lastId
                  }
                })

                const pageData: any[] = res.list || []
                lastId = res.lastId || null

                // 如果当前页没有数据，说明已经查询完所有数据
                if (!pageData || pageData.length === 0) {
                  hasMoreData = false
                  break
                }

                // 将当前页数据添加到总数据中
                allData = allData.concat(pageData)

                if (pageData.length < size) {
                  hasMoreData = false
                  break
                }

                // 继续查询下一页
                current++

                // 更新提示信息
                // Message.info(`已查询 ${allData.length} 条数据，继续查询第 ${current} 页...`)
              } catch (pageError: any) {
                // 如果查询出错，停止查询
                console.error(`查询第 ${current} 页时出错:`, pageError)
                hasMoreData = false

                // 如果已经有数据，继续导出；如果没有数据，提示错误
                if (allData.length === 0) {
                  throw pageError
                }
                break
              }
            }

            // 4. 检查是否有数据可导出
            if (!allData || allData.length === 0) {
              Message.warning('没有可导出的数据')
              return
            }

            // Message.info(`共查询到 ${allData.length} 条数据，正在准备导出...`)

            // 5. 获取列配置
            const columns = [
              { label: '发货单号', prop: 'shipmentCode' },
              {
                label: '发货单状态',
                prop: 'shipmentStatus',
                formatter: (row: any) => {
                  return (ShipmentStatusName as any)[row.shipmentStatus] || row.shipmentStatus
                }
              },
              { label: '运单号', prop: 'waybillNo' },
              { label: '发货渠道', prop: 'distributorName' },
              { label: '发货商品编号', prop: 'productCode' },
              { label: '发货商品', prop: 'productName' },
              { label: '创建人', prop: 'creator' },
              {
                label: '创建时间',
                prop: 'gmtCreate',
                formatter: (row: any) => {
                  return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              },
              {
                label: '发货时间',
                prop: 'shipTime',
                formatter: (row: any) => {
                  return row.shipTime ? dayjs(row.shipTime).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              },
              {
                label: '入库时间',
                prop: 'inTime',
                formatter: (row: any) => {
                  return row.inTime ? dayjs(row.inTime).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              },
              {
                label: '出库/退件时间',
                prop: 'outTime',
                formatter: (row: any) => {
                  return row.outTime ? dayjs(row.outTime).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              },
              { label: '滞留时长（小时）', prop: 'strandedHours' },
              { label: '收货门店编号', prop: 'receiveShopCode' },
              { label: '收货门店名称', prop: 'receiveShopName' },
              {
                label: '收货门店省市区',
                prop: 'receiveProvince',
                formatter: (row: any) => {
                  return `${row?.receiveProvince || ''}${row?.receiveCity || ''}${row?.receiveDistrict || ''}`
                }
              },
              { label: '收货详细地址', prop: 'receiveAddress' },
              { label: '收货人', prop: 'receiveName' },
              { label: '收货人手机号', prop: 'receiveMobile' },
              { label: '入库门店编号', prop: 'inShopCode' },
              { label: '入库门店名称', prop: 'inShopName' },
              { label: '入库柜机编号', prop: 'inCabinetCode' },
              { label: '入库柜机序号', prop: 'inCabinetSerialNumber' },
              { label: '入库柜机格口', prop: 'inCabinetCell' },
              {
                label: '入库柜机地址',
                prop: 'inCabinetProvince',
                formatter: (row: any) => {
                  return `${row?.inCabinetProvince || ''}${row?.inCabinetCity || ''}${row?.inCabinetDistrict || ''}${
                    row?.inCabinetAddress || ''
                  }`
                }
              },
              { label: '取件码', prop: 'inPickupCode' },
              { label: '入库快递公司', prop: 'inExpressName' },
              {
                label: '出库类型',
                prop: 'outType',
                formatter: (row: any) => {
                  return (OutTypeName as any)[row.outType] || row.outType
                }
              }
            ]

            // 6. 格式化数据（应用 formatter）
            const formattedData = allData.map((row: any) => {
              const formattedRow: any = {}
              columns.forEach((col: any) => {
                if (col.formatter) {
                  formattedRow[col.prop] = col.formatter(row)
                } else {
                  formattedRow[col.prop] = row[col.prop] || '--'
                }
              })
              return formattedRow
            })

            const exportDataFn = app.useComponentsConfig('xlsx.exportData')

            if (exportDataFn) {
              exportDataFn(formattedData, {
                columns: columns,
                fileName: '发货单列表',
                app
              })
            }
            Message.success(`导出成功，共导出 ${formattedData.length} 条数据`)
          } catch (error: any) {
            console.error('导出失败:', error)
            // Message.error(error?.message || '导出失败，请稍后重试')
          } finally {
            // 关闭全屏loading
            loadingInstance.close()
          }
        }
      }
    },
    searchOn: [UI_PAGE_EVENTS.ACTIVATED],
    search: {
      beforeSearch: ({ app, searchParams }: any) => {
        const basic = app.stores.userStore?.data?.basic || {}
        if (basic.belongType === RbacRoleScopeType.SUPPLIER) {
          searchParams.supplierCode = basic.supplierOrgCode
        }
      },
      afterSearch: ({ app, searchParams }: PageContext) => {
        app.emits('$page_sum_search')
      },
      items: [
        {
          label: '渠道商',
          type: 'select',
          prop: 'distributorCode',
          options: commonOptions.distributor,
          span: 6
        },
        {
          // type: 'fuzzy-select',
          prop: 'supplierCode',
          label: '供应商',
          optionValueProp: 'supplierCode',
          optionLabelProp: 'supplierName',
          span: 6,
          dynamicAttrs: ({ data, app }: PageContext) => {
            const basic = app.stores.userStore?.data?.basic || {}
            if (basic.belongType === RbacRoleScopeType.SUPPLIER) {
              return {
                default: basic.supplierOrgCode,
                type: 'select',
                options: [
                  {
                    supplierName: basic.supplierOrgName,
                    supplierCode: basic.supplierOrgCode
                  }
                ],
                disabled: true
              }
            }
            return {
              type: 'fuzzy-select',
              api: '/cabinet/admin/base/AdminBaseSupplierQueryController/queryList',
              apiParams: {
                current: 1,
                size: 500
              },
              triggerFocus: true,
              remote: true,
              keywordProp: 'supplierName'
            }
          }
        },
        { type: 'input', prop: 'productCode', label: '商品编码', span: 6 },
        { type: 'input', prop: 'productName', label: '商品名称', span: 6 },
        // { type: 'input', prop: 'receiveShopCode', label: '门店编号', span: 6 },
        {
          type: 'fuzzy-select',
          prop: 'receiveShopCode',
          // labelProp: 'shopName',
          label: '门店编号',
          optionValueProp: 'shopCode',
          optionLabelProp: 'shopName',
          triggerFocus: true,
          remote: true,
          keywordProp: 'shopName',
          api: '/cabinet/admin/base/AdminBaseShopQueryController/queryList',
          span: 6
        },
        {
          label: '发货单状态',
          type: 'select',
          prop: 'shipmentStatus',
          options: commonOptions.shipmentStatus,
          span: 6
        },
        { type: 'input', prop: 'strandedHours', label: '滞留时长', span: 6 },
        { type: 'input', prop: 'shipmentCode', label: '发货单号', span: 6 },
        { type: 'input', prop: 'waybillNo', label: '运单号', span: 6 },
        {
          label: '创建时间',
          type: 'date-range-picker',
          prop: 'gmtCreateStart',
          toProp: 'gmtCreateEnd',
          defaultTo: 'now',
          defaultRange: 30
          // maxRange: 730
        }
      ]
    },
    toolbar: {
      noExport: true,
      items: [
        { action: 'add' },
        { action: 'batchChange' },
        { action: 'import' },
        { action: 'customExport' },
        {
          cmpt: 'CCabinetShipmentStatistics',
          listeners: {
            sumSearchOn: '$page_sum_search'
          }
        }
      ]
    },
    table: (ctx: PageContext) => {
      return {
        showCheckbox: true,
        checkboxSelectable: (row: any) => {
          // 待发货、待入库、待出库
          return [ShipmentStatus.WAIT_SHIPMENT, ShipmentStatus.WAIT_INBOUND, ShipmentStatus.WAIT_OUTBOUND].includes(
            row.shipmentStatus
          )
        },

        innerAttrs: {
          onSelectionChange: (selection: any) => {
            ctx.app.setPageData('checkedArr', selection)
          }
        },
        operation: {
          width: 120,
          items: [
            {
              action: 'close',
              // 待发货
              visibleOn: `['${ShipmentStatus.WAIT_SHIPMENT}'].includes(this.data.row.shipmentStatus)`
            },
            { action: 'detail' },
            { action: 'log' }
          ]
        },
        columns: [
          { label: '发货单号', prop: 'shipmentCode', minWidth: 180 },
          {
            label: '发货单状态',
            minWidth: 120,
            formatter: (row: any) => {
              return (ShipmentStatusName as any)[row.shipmentStatus]
            }
          },
          { label: '运单号', prop: 'waybillNo', minWidth: 180 },
          { label: '发货渠道', prop: 'distributorName', minWidth: 100 },
          { label: '发货商品编号', prop: 'productCode', minWidth: 180 },
          { label: '发货商品', prop: 'productName', minWidth: 120 },
          { label: '创建人', prop: 'creator', minWidth: 150 },
          {
            label: '创建时间',
            minWidth: 150,
            formatter: (row: any) => {
              return dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') // 时间格式化
            }
          },
          {
            label: '发货时间',
            minWidth: 150,
            formatter: (row: any) => {
              return row.shipTime ? dayjs(row.shipTime).format('YYYY-MM-DD HH:mm:ss') : '--'
            }
          },
          {
            label: '入库时间',
            minWidth: 150,
            formatter: (row: any) => {
              return row.inTime ? dayjs(row.inTime).format('YYYY-MM-DD HH:mm:ss') : '--'
            }
          },
          {
            label: '出库/退件时间',
            minWidth: 150,
            formatter: (row: any) => {
              return row.outTime ? dayjs(row.outTime).format('YYYY-MM-DD HH:mm:ss') : '--'
            }
          },
          { label: '滞留时长（小时）', prop: 'strandedHours', minWidth: 120 },
          { label: '收货门店编号', prop: 'receiveShopCode', minWidth: 120 },
          { label: '收货门店名称', prop: 'receiveShopName', minWidth: 120 },
          {
            label: '收货门店省市区',
            minWidth: 180,
            formatter: (row: any) => {
              return `${row?.receiveProvince}${row?.receiveCity}${row?.receiveDistrict}`
            }
          },
          { label: '收货详细地址', prop: 'receiveAddress', minWidth: 180 },
          { label: '收货人', prop: 'receiveName', minWidth: 120 },
          { label: '收货人手机号', prop: 'receiveMobile', minWidth: 120 }
        ]
      }
    }
  }
}
